import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api';

/**
 * Categorized listing of all help articles, with a search box.
 *
 * Shared between the standalone /help page (Help.jsx) and the in-drawer
 * browse view (HelpChatPanel's browse mode). Article clicks delegate to
 * the caller:
 *
 *   - standalone page → ``ArticleLink`` (react-router Link to /help/<slug>)
 *   - drawer          → ``onArticleClick(slug)`` (push article-view state)
 *
 * Either ``ArticleLink`` or ``onArticleClick`` MUST be provided; if both
 * are present the component prefers ``ArticleLink`` so the standalone
 * page keeps middle-click + open-in-new-tab semantics.
 */
export default function HelpBrowseView({ ArticleLink, onArticleClick }) {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    api.get('/help/docs')
      .then((res) => setGroups(res.groups))
      .catch((e) => setError(e.message));
  }, []);

  const filteredGroups = useMemo(() => {
    if (!groups) return null;
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        articles: g.articles.filter((a) => a.title.toLowerCase().includes(q)),
      }))
      .filter((g) => g.articles.length > 0);
  }, [groups, query]);

  const renderArticle = (a) => {
    if (ArticleLink) {
      return (
        <ArticleLink slug={a.slug} className="help-article-link">
          {a.title}
        </ArticleLink>
      );
    }
    return (
      <a
        href={`/help/${a.slug}`}
        className="help-article-link"
        onClick={(e) => {
          if (onArticleClick) {
            e.preventDefault();
            onArticleClick(a.slug);
          }
        }}
      >
        {a.title}
      </a>
    );
  };

  return (
    <>
      <input
        type="search"
        className="help-search"
        placeholder="Search help…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!groups && !error && <p>Loading…</p>}

      {filteredGroups && filteredGroups.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)' }}>
          Nothing matches "{query}". Try the help chat.
        </p>
      )}

      {filteredGroups?.map((g) => (
        <section className="help-category" key={g.category}>
          <div className="help-category-label">{g.label}</div>
          <ul className="help-article-list">
            {g.articles.map((a) => (
              <li key={a.slug}>{renderArticle(a)}</li>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}
